import { SOCIAL_LINKS } from "./nav";
import COPY from "./content/siteCopy.json";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";

const API = process.env.REACT_APP_BACKEND_URL;
const SITE = "https://www.hellomortgage.ca";
let aggCache = null;

export function useTestimonialAggregate() {
  const [agg, setAgg] = useState(aggCache);
  useEffect(() => {
    if (aggCache) return;
    Promise.all([fetch(`${API}/api/public/testimonials/aggregate`).then((r) => r.json()), fetch(`${API}/api/public/testimonials/schema-reviews`).then((r) => r.json())])
      .then(([a, s]) => { aggCache = { ...a, reviews: s.items }; setAgg(aggCache); }).catch(() => {});
  }, []);
  return agg;
}

// "150+" — published Google review count rounded down to the nearest ten; replaces the {reviews} token in copy.
export const reviewsLabel = (count) => (count ? `${Math.max(10, Math.floor(count / 10) * 10)}+` : "150+");
export function useReviewsLabel() {
  return reviewsLabel(useTestimonialAggregate()?.count);
}

// Global FinancialService + AggregateRating + Review schema — this is what produces the ★ 5.0 (144) rich result.
export function ReviewSchema() {
  const agg = useTestimonialAggregate();
  if (!agg?.count) return null;
  const ld = { "@context": "https://schema.org", "@type": "FinancialService", "@id": `${SITE}/#organization`, name: "Hello Mortgage Team", slogan: "Say Hello To The Easiest Way To Mortgage", url: `${SITE}/`,
    sameAs: SOCIAL_LINKS.map(([, href]) => href),
    image: `${SITE}/brand/hello-mortgage-team-logo-transparent.png`, logo: { "@type": "ImageObject", url: `${SITE}/brand/hello-mortgage-team-logo-transparent.png` }, telephone: "+1-780-900-2495", priceRange: "Free consultation",
    foundingDate: "2020", founder: { "@type": "Person", "@id": `${SITE}/#matt`, name: "Matt Broom-Hall", jobTitle: "Founder & Mortgage Broker", image: `${SITE}/brand/matt-broom-hall.png`, url: `${SITE}/why-hello/#team` },
    parentOrganization: { "@type": "Organization", name: "BRX Mortgage" },
    address: { "@type": "PostalAddress", addressRegion: "AB", addressCountry: "CA" }, areaServed: { "@type": "State", name: "Alberta" },
    aggregateRating: { "@type": "AggregateRating", ratingValue: agg.rating, bestRating: 5, worstRating: 1, reviewCount: agg.count },
    review: (agg.reviews || []).map((r) => ({ "@type": "Review", author: { "@type": "Person", name: r.name }, datePublished: r.date || undefined,
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 }, reviewBody: r.text })) };
  return <Helmet><script type="application/ld+json" data-testid="review-schema">{JSON.stringify(ld)}</script></Helmet>;
}

const Stars = ({ n = 5, className = "" }) => <span className={`tst-stars ${className}`} aria-label={`${n} out of 5 stars`}>{"★★★★★".slice(0, n)}</span>;
const GoogleG = () => (
  <svg viewBox="0 0 48 48" width="22" height="22" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.4l6.8-6.8C35.8 2.3 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.4 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.7 6c4.5-4.2 6.9-10.3 6.9-17.7z"/><path fill="#FBBC05" d="M10.5 28.7A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.9-6.1z"/><path fill="#34A853" d="M24 48c6.3 0 11.7-2.1 15.6-5.7l-7.7-6c-2.1 1.4-4.8 2.3-7.9 2.3-6.3 0-11.6-3.9-13.5-9.8l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
);

const COMMA = "M50 10 C22 10 0 32 0 60 C0 88 22 110 50 110 C56 110 62 109 68 107 C60 140 40 160 12 172 L24 190 C76 172 100 130 100 70 C100 37 78 10 50 10 Z";
const QuoteMark = ({ close = false }) => (
  <svg className={`tst-qm ${close ? "close" : "open"}`} viewBox="0 0 230 215" aria-hidden="true" style={close ? undefined : { transform: "rotate(180deg)" }}>
    <g fill="none" stroke="var(--cream)" strokeWidth="7" transform="translate(16,14)"><path d={COMMA} /><path d={COMMA} transform="translate(112,0)" /></g>
    <g fill="var(--pink)" transform="translate(4,4)"><path d={COMMA} /><path d={COMMA} transform="translate(112,0)" /></g>
  </svg>
);

export function Testimonials({ topics = [], city = "", region = [], limit = 0, eyebrow = COPY.reviews.eyebrow, heading = COPY.reviews.heading }) {
  const [data, setData] = useState(null);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const [open, setOpen] = useState(null);
  const touch = useRef(null);
  const key = `${topics.join(",")}|${city}|${region.join(",")}`;
  useEffect(() => {
    const qs = new URLSearchParams({ topics: topics.join(","), city, region: region.join(","), ...(limit ? { limit: String(limit) } : {}) });
    fetch(`${API}/api/public/testimonials?${qs}`).then((r) => r.json()).then((d) => { setData(d); setI(0); }).catch(() => setData({ items: [], aggregate: {} }));
  }, [key, limit]); // eslint-disable-line react-hooks/exhaustive-deps
  const items = data?.items || [];
  useEffect(() => {
    if (paused || open || items.length < 2) return undefined;
    const t = setInterval(() => setI((v) => (v + 1) % items.length), 7000);
    return () => clearInterval(t);
  }, [paused, open, items.length]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  if (!data || !items.length) return null;
  const cur = items[i];
  const agg = data.aggregate || {};
  const rounded = reviewsLabel(agg.count).slice(0, -1);
  const go = (d) => setI((v) => (v + d + items.length) % items.length);
  return (
    <section className="tst" aria-labelledby="reviews-heading" data-testid="testimonials" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }} onTouchEnd={(e) => { if (touch.current == null) return; const dx = e.changedTouches[0].clientX - touch.current; if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1); touch.current = null; }}>
      <div className="wrap tst-grid">
        <div className="tst-brag">
          <div className="tst-source"><span className="tst-g"><GoogleG /></span><span className="tst-source-lines"><span className="eyebrow" style={{ color: "#fff", margin: 0 }}>{eyebrow}</span>{city && <span className="eyebrow" style={{ color: "#fff", margin: 0 }} data-testid="reviews-top-rated">Top Rated Mortgage Broker in {city}</span>}</span></div>
          <h2 id="reviews-heading" className="display">{heading}</h2>
          <p className="tst-tag" aria-hidden="true">#humblebrag</p>
          <p className="tst-count" data-testid="testimonials-count">Hello Mortgage Team has over <strong>{rounded}</strong> <Stars /> reviews!</p>
          <a className="tst-link" href={data.google_url} target="_blank" rel="noopener noreferrer" data-testid="testimonials-google-link">Read our reviews on Google</a>
        </div>
        <figure className="tst-bubble-wrap">
          <QuoteMark />
          <blockquote className="tst-bubble" data-testid="testimonial-quote">
            <svg className="tst-shape" viewBox="236 62 450 386" preserveAspectRatio="none" aria-hidden="true"><path d="M243 70 L678 140 L620 355 L300 360 L243 440 Z" fill="#fff" stroke="#fff" strokeWidth="14" strokeLinejoin="round" /></svg>
            <div className="tst-words" key={cur.id}>
              <div className="tst-quote"><p>{cur.excerpt || cur.text}</p></div>
              {cur.excerpt && cur.excerpt !== cur.text && <button type="button" className="tst-more" onClick={() => setOpen(cur)} data-testid="testimonial-toggle">Read the full review</button>}
              <figcaption><Stars n={cur.rating || 5} className="small" /><span className="tst-name" data-testid="testimonial-name">— {cur.name}</span>{cur.location && <span className="tst-loc" data-testid="testimonial-location">{cur.location}</span>}<span className="tst-pos" data-testid="testimonial-position">{i + 1} / {items.length}</span></figcaption>
            </div>
          </blockquote>
          <QuoteMark close />
        </figure>
      </div>
      {open && (
        <div className="tst-modal" role="dialog" aria-modal="true" aria-label={`Full review from ${open.name}`} onClick={() => setOpen(null)} data-testid="testimonial-modal">
          <div className="tst-modal-card" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="tst-modal-close" onClick={() => setOpen(null)} aria-label="Close" data-testid="testimonial-modal-close">×</button>
            <Stars n={open.rating || 5} className="small" />
            <p data-testid="testimonial-modal-text">{open.text}</p>
            <span className="tst-name">— {open.name}{open.location && <span className="tst-loc-inline"> · {open.location}</span>}</span>
          </div>
        </div>
      )}
    </section>
  );
}
